import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface MovieCardProps {
  movie: Movie;
  variant?: 'default' | 'compact' | 'featured';
}

export default function MovieCard({ movie, variant = 'default' }: MovieCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/movie/${movie.id}`}
        className="group block relative overflow-hidden rounded-xl bg-background-100 border border-background-300/30 hover:border-primary-500/50 transition-all duration-300 cursor-pointer"
      >
        <div className="flex flex-col sm:flex-row">
          <div className="relative w-full sm:w-[200px] h-[280px] sm:h-auto shrink-0 overflow-hidden">
            <img
              src={movie.poster}
              alt={movie.title}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
            {movie.isNew && (
              <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-accent-500 text-background-950 text-xs font-semibold whitespace-nowrap">
                新上映
              </span>
            )}
          </div>
          <div className="flex-1 p-5 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded-full bg-primary-500/20 text-primary-500 text-xs font-medium whitespace-nowrap">
                  {movie.category}
                </span>
                <span className="text-xs text-foreground-500">{movie.year}</span>
              </div>
              <h4 className="text-lg font-heading font-bold text-foreground-50 group-hover:text-primary-500 transition-colors mb-1">
                {movie.title}
              </h4>
              <p className="text-xs text-foreground-500 mb-3">{movie.engTitle}</p>
              <p className="text-sm text-foreground-400 leading-relaxed line-clamp-2">
                {movie.description}
              </p>
            </div>
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-background-300/20">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-primary-500 text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-100">{movie.rating}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line text-foreground-500 text-sm"></i>
                </div>
                <span className="text-sm text-foreground-500">{movie.duration}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 ml-auto">
                {movie.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded-full bg-background-200/50 text-foreground-500 text-xs whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/movie/${movie.id}`}
      className="group block relative overflow-hidden rounded-xl bg-background-100 border border-background-300/30 hover:border-primary-500/50 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[2/3] overflow-hidden">
        <img
          src={movie.poster}
          alt={movie.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-transparent to-transparent"></div>

        {movie.isNew && (
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-accent-500 text-background-950 text-xs font-semibold whitespace-nowrap">
            新上映
          </span>
        )}

        {movie.isHot && (
          <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-primary-500 text-background-950 text-xs font-semibold whitespace-nowrap">
            热播
          </span>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-center gap-1.5 mb-1.5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-star-fill text-primary-500 text-xs"></i>
            </div>
            <span className="text-sm font-semibold text-foreground-50">{movie.rating}</span>
          </div>
          <h4 className="text-base font-heading font-semibold text-foreground-50 group-hover:text-primary-500 transition-colors mb-1">
            {movie.title}
          </h4>
          <div className="flex items-center gap-2 text-xs text-foreground-400">
            <span>{movie.year}</span>
            <span>·</span>
            <span>{movie.category}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}